import { PostHogProvider } from '@posthog/react';
import type { AppUser } from 'api/types';
import type { PropsWithChildren } from 'react';
import posthog from 'posthog-js';

type AnalyticsProperties = Record<string, unknown>;

const posthogKey = process.env.REACT_APP_POSTHOG_KEY?.trim() ?? '';
const posthogHost = process.env.REACT_APP_POSTHOG_HOST?.trim() ?? '';
const isProductionBuild = process.env.NODE_ENV === 'production';

export const isAnalyticsEnabled =
  typeof window !== 'undefined' && isProductionBuild && Boolean(posthogKey && posthogHost);

const posthogOptions = {
  api_host: posthogHost,
  defaults: '2026-01-30',
  cross_subdomain_cookie: true,
} as const;

const getPageProperties = (): AnalyticsProperties => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);

  return {
    environment: 'prod',
    site_context: 'app',
    entry_path: window.location.pathname,
    destination_path: window.location.pathname,
    referrer: document.referrer || undefined,
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
  };
};

export const AnalyticsProvider = ({ children }: PropsWithChildren) => {
  if (!isAnalyticsEnabled) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
      {children}
    </PostHogProvider>
  );
};

export const capture = (event: string, properties: AnalyticsProperties = {}) => {
  if (!isAnalyticsEnabled) return;

  posthog.capture(event, {
    ...getPageProperties(),
    ...properties,
  });
};

export const identifyUser = (
  user: Pick<AppUser, 'userId' | 'tenantId' | 'email' | 'firstName' | 'lastName' | 'role'>
) => {
  if (!isAnalyticsEnabled) return;

  posthog.identify(user.userId, {
    environment: 'prod',
    site_context: 'app',
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    tenantId: user.tenantId,
    role: user.role,
  });
};

export const resetAnalytics = () => {
  if (!isAnalyticsEnabled) return;
  posthog.reset();
};
